<template>
  <app-content class="profile-edit">
    <step-by-step
      class="profile-edit__step-by-step"
      :steps="steps"
      :loading="loading"
      :current-step.sync="step"
      :next-disabled="!canForward"
      :conclude-disabled="!saveEnabled"
      @conclude="save()"
    >
      <profile-info
        slot="content:profile-info"
        :profile="profile"
        :valid.sync="stepValid"
        :origin-parent-profile="originParentProfile"
      >
      </profile-info>
      <companies
        slot="content:companies"
        :profile="profile"
        @setInitialValues:companies="setInitialValuesCompanies"
      >
      </companies>
      <installations
        slot="content:installations"
        :profile="profile"
        @setInitialValues:installations="setInitialValuesInstallations"
      >
      </installations>
      <assets
        slot="content:assets"
        :profile="profile"
        @setInitialValues:assets="setInitialValuesAssets"
      >
      </assets>
      <modules
        slot="content:modules"
        :profile="profile"
        @setInitialValues:modules="setInitialValuesModules"
      >
      </modules>
      <template slot="bottomPreviousPanel">
        <b-button
          v-if="canCancel"
          class="profile-edit__cancel-button"
          variant="secondary"
          @click="cancel()"
        >
          {{ $t('general.cancel') }}
        </b-button>
      </template>
    </step-by-step>
  </app-content>
</template>

<script>
import StepByStep from '@/components/stepByStep/index';
import ProfileEditService from '@/domain/services/generalPanel/adminPanel/profile/profileEditService';
import ProfileSaveValidation from '@/domain/services/generalPanel/adminPanel/profile/profileSaveValidation';
import ProfileInfo from '@/domain/components/generalPanel/adminPanel/profile/editing/Info';
import Companies from '@/domain/components/generalPanel/adminPanel/profile/editing/Companies';
import Installations from '@/domain/components/generalPanel/adminPanel/profile/editing/Installations';
import Assets from '@/domain/components/generalPanel/adminPanel/profile/editing/Assets';
import Modules from '@/domain/components/generalPanel/adminPanel/profile/editing/Modules';

export default {
  components: {
    StepByStep,
    ProfileInfo,
    Companies,
    Installations,
    Assets,
    Modules,
  },
  data: () => ({
    profile: {
      users: [],
      administrators: [],
      companies: [],
      installations: [],
      assets: [],
      modules: [],
      parentProfileId: null,
    },
    originParentProfile: null,
    loading: false,
    initialValues: {
      companies: null,
      installations: null,
      assets: null,
      modules: null,
    },
    stepValid: false,
    step: null,
    steps: [
      {
        labelPath: 'profile-manager.editing.info.title',
        icon: 'fas fa-info-circle',
        name: 'profile-info',
      },
      {
        labelPath: 'profile-manager.editing.companies.title',
        icon: 'fas fa-building',
        name: 'companies',
      },
      {
        labelPath: 'profile-manager.editing.installations.title',
        icon: 'fas fa-broadcast-tower',
        name: 'installations',
      },
      {
        labelPath: 'profile-manager.editing.assets.title',
        icon: 'fas fa-boxes',
        name: 'assets',
      },
      {
        labelPath: 'profile-manager.editing.modules.title',
        icon: 'fas fa-box',
        name: 'modules',
      },
    ],
  }),
  computed: {
    canCancel() {
      return this.step === 0;
    },
    canForward() {
      switch (this.step) {
        case 0:
          return this.getCanForwardInfo();
        case 1:
          return this.getCanForwardCompanies();
        case 2:
          return this.getCanForwardInstallations();
        case 3:
          return this.getCanForwardAssets();
        default:
          return true;
      }
    },
    saveEnabled() {
      return this.canTrySave();
    },
    hasAnyAccessRemoved() {
      return ProfileSaveValidation
        .verifyIfAnyAccessRemoved(this.profile, this.initialValues);
    },
  },
  created() {
    if (this.$route.params.profileId) {
      ProfileEditService.getProfileForEdit(this.$route.params.profileId)
        .then((profile) => {
          this.profile = {
            ...profile,
            modules: [],
            assets: [],
            companies: [],
            installations: [],
          };
          this.originParentProfile = profile.originParentProfile;
        })
        .catch(() => {
          this.$router.push({ name: 'admin-panel', params: { activeTabName: 'profiles' } });
        });
    }
  },
  methods: {
    cancel() {
      this.$confirmMessage({
        code: 'MC006',
      }).accepted(() => {
        this.$router.push({ name: 'admin-panel', params: { activeTabName: 'profiles' } });
      });
    },
    getCanForwardInfo() {
      return this.stepValid;
    },
    getCanForwardCompanies() {
      return this.profile && (this.profile.companies || []).length;
    },
    getCanForwardInstallations() {
      return this.profile && (this.profile.installations || []).length;
    },
    getCanForwardAssets() {
      return this.profile && (this.profile.assets || []).length;
    },
    canTrySave() {
      return this.profile && (this.profile.modules || []).length;
    },
    saveProfile() {
      this.loading = true;
      ProfileEditService.saveProfile(this.profile)
        .then(() => {
          this.$pushAlert({ code: 'MS006' });
          this.$router.push({ name: 'admin-panel', params: { activeTabName: 'profiles' } });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    save() {
      this.$confirmMessage(this.getSaveConfirmMessage())
        .accepted(() => this.saveProfile());
    },
    getSaveConfirmMessage() {
      if (this.profile.administrators.length > 1) {
        return {
          code: 'MC059',
        };
      }
      if (!this.profile.id) {
        return {
          code: 'MC011',
        };
      }
      if (this.hasAnyAccessRemoved) {
        return {
          code: 'MC025',
          messageParams: { NomeDoPerfil: this.profile.name },
        };
      }
      return {
        code: 'MC011',
      };
    },
    setInitialValuesCompanies(data) {
      this.initialValues.companies = data;
    },
    setInitialValuesAssets(data) {
      this.initialValues.assets = data;
    },
    setInitialValuesModules(data) {
      this.initialValues.modules = data;
    },
    setInitialValuesInstallations(data) {
      this.initialValues.installations = data;
    },
  },
};
</script>

<style lang="sass" scoped>
  @import "src/styles/defaults/variables"
  @import "src/styles/defaults/mobile-first"

  .profile-edit
    &__step-by-step
      flex: 1
    +media-min-sm()
    +media-min-md()
    +media-min-lg()
</style>
